import React from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { tokens, SEMANTIC } from "./tokens";

/**
 * Signal Bar — el anti-KPI-tile (§5.1 del sistema de diseño).
 *
 * Franja horizontal compacta con 4-6 métricas separadas por divisores finos.
 * Cada métrica: label pequeño, valor grande en mono tabular, tendencia opcional.
 *
 * @param {Object[]} signals
 * @param {string}   signals[].label    - etiqueta corta en mayúsculas
 * @param {string|number} signals[].value - valor principal
 * @param {string}   [signals[].trend]  - texto de tendencia, ej. "▲4%" / "▼ $12k"
 * @param {"ok"|"warn"|"crit"|"info"|"muted"} [signals[].tone] - color del valor
 * @param {string}   [signals[].hint]   - tooltip explicativo
 */
export default function SignalBar({ signals = [], sx }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        bgcolor: tokens.surface,
        border: `1px solid ${tokens.line}`,
        borderRadius: "6px",
        overflowX: "auto",
        minHeight: 56,
        ...sx,
      }}
    >
      {signals.map((s, i) => {
        const tone = s.tone ? SEMANTIC[s.tone] : null;
        const metric = (
          <Stack
            key={s.label}
            justifyContent="center"
            sx={{
              px: 2.5,
              py: 1,
              minWidth: 120,
              borderLeft: i > 0 ? `1px solid ${tokens.line}` : "none",
              flex: "1 1 auto",
            }}
          >
            <Typography
              sx={{
                fontFamily: tokens.fontUI,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: tokens.ink3,
                whiteSpace: "nowrap",
              }}
            >
              {s.label}
            </Typography>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography
                sx={{
                  fontFamily: tokens.fontMono,
                  fontVariantNumeric: "tabular-nums",
                  fontSize: 20,
                  fontWeight: 600,
                  color: tone ? tone.fg : tokens.ink,
                  whiteSpace: "nowrap",
                }}
              >
                {s.value}
              </Typography>
              {s.trend && (
                <Typography
                  sx={{
                    fontFamily: tokens.fontMono,
                    fontSize: 12,
                    color: tokens.ink2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {s.trend}
                </Typography>
              )}
            </Stack>
          </Stack>
        );
        return s.hint ? (
          <Tooltip key={s.label} title={s.hint} arrow>
            {metric}
          </Tooltip>
        ) : (
          metric
        );
      })}
    </Box>
  );
}
